/**
 * The client's copy of the workspace. Each entity type is one table
 * (id -> row) kept in the TanStack Query cache under ["entities", name].
 * The sync engine fills it from /api/bootstrap and patches it with live
 * deltas and optimistic edits; components read it with the hooks below.
 */
import { QueryClient } from "@tanstack/react-query";
import { useMemo, useSyncExternalStore } from "react";
import type { EntityMap, EntityName, Id } from "./types";

export const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
});
// Tables are replaced immutably by the sync engine; deep comparison would be wasted work.
queryClient.setQueryDefaults(["entities"], { structuralSharing: false, staleTime: Infinity, gcTime: Infinity });

export type Table<T> = Readonly<Record<Id, T>>;
const EMPTY: Table<never> = Object.freeze({});
const tableKey = (e: EntityName) => ["entities", e] as const;

const listeners = new Map<string, Set<() => void>>();
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated" && event.type !== "added" && event.type !== "removed") return;
  const [scope, name] = event.query.queryKey as [string, string];
  if (scope === "entities") listeners.get(name)?.forEach((l) => l());
});

function subscribe(e: EntityName, cb: () => void) {
  let set = listeners.get(e);
  if (!set) listeners.set(e, (set = new Set()));
  set.add(cb);
  return () => set!.delete(cb);
}

export function getTable<E extends EntityName>(e: E): Table<EntityMap[E]> {
  return (queryClient.getQueryData(tableKey(e)) as Table<EntityMap[E]> | undefined) ?? EMPTY;
}

export function getRow<E extends EntityName>(e: E, id: Id | null | undefined): EntityMap[E] | undefined {
  return id ? getTable(e)[id] : undefined;
}

export function setTable<E extends EntityName>(e: E, update: (t: Record<Id, EntityMap[E]>) => Record<Id, EntityMap[E]>) {
  queryClient.setQueryData(tableKey(e), (old: Table<EntityMap[E]> | undefined) => update({ ...(old ?? EMPTY) }));
}

/** Adds rows fetched on demand (comments, notifications, archived issues). */
export function mergeRows<E extends EntityName>(e: E, rows: EntityMap[E][]) {
  if (!rows.length) return;
  setTable(e, (t) => {
    for (const r of rows) t[(r as { id: Id }).id] = r;
    return t;
  });
}

export function useTable<E extends EntityName>(e: E): Table<EntityMap[E]> {
  return useSyncExternalStore(
    (cb) => subscribe(e, cb),
    () => getTable(e),
  );
}

/** One row; re-renders only when that row changes. */
export function useRow<E extends EntityName>(e: E, id: Id | null | undefined): EntityMap[E] | undefined {
  return useSyncExternalStore(
    (cb) => subscribe(e, cb),
    () => (id ? getTable(e)[id] : undefined),
  );
}

type WithArchive = { archivedAt?: string | null };

/** Live rows of a type, optionally filtered and sorted. */
export function useRows<E extends EntityName>(
  e: E,
  filter?: (row: EntityMap[E]) => boolean,
  sort?: (a: EntityMap[E], b: EntityMap[E]) => number,
  deps: unknown[] = [],
): EntityMap[E][] {
  const table = useTable(e);
  return useMemo(() => {
    let rows = Object.values(table).filter((r) => !(r as WithArchive).archivedAt);
    if (filter) rows = rows.filter(filter);
    if (sort) rows.sort(sort);
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, ...deps]);
}

export function indexById<T extends { id: Id }>(rows: T[]): Record<Id, T> {
  const out: Record<Id, T> = {};
  for (const r of rows) out[r.id] = r;
  return out;
}
